import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';
import { ScheduleException, HolidaySetting, Holiday } from '../../../types/working-hours';
import ExceptionFormPanel from './ExceptionFormPanel';

const REGION_NAMES: Record<string, string> = {
  US: 'United States',
  UK: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  ES: 'Spain',
  IT: 'Italy',
  CA: 'Canada',
  AU: 'Australia',
};

interface ExceptionsTabProps {
  exceptions: ScheduleException[];
  isLoading: boolean;
}

const ExceptionsTab: React.FC<ExceptionsTabProps> = ({
  exceptions,
  isLoading,
}) => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingException, setEditingException] = useState<ScheduleException | null>(null);

  // Fetch holiday settings
  const { data: holidaySettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['holiday-settings'],
    queryFn: async () => {
      const response = await expertDashboardApi.holidaySettings.get();
      return response.data.holiday_setting as HolidaySetting;
    },
  });

  // Fetch upcoming holidays
  const { data: upcomingHolidays = [] } = useQuery({
    queryKey: ['upcoming-holidays'],
    queryFn: async () => {
      const response = await expertDashboardApi.holidaySettings.getUpcoming();
      return response.data.holidays as Holiday[];
    },
    enabled: !!holidaySettings?.enabled,
  });

  // Update holiday settings
  const updateSettingsMutation = useMutation({
    mutationFn: (data: { region_code?: string; enabled?: boolean }) =>
      expertDashboardApi.holidaySettings.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holiday-settings'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-holidays'] });
      toast.success('Holiday settings updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    },
  });

  // Delete exception
  const deleteMutation = useMutation({
    mutationFn: (id: string) => expertDashboardApi.scheduleExceptions.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-exceptions'] });
      toast.success('Exception deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete exception');
    },
  });

  const handleAddException = () => {
    setEditingException(null);
    setIsFormOpen(true);
  };

  const handleEditException = (exception: ScheduleException) => {
    setEditingException(exception);
    setIsFormOpen(true);
  };

  const handleDeleteException = (exception: ScheduleException) => {
    const dateOpts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    const startStr = new Date(exception.date).toLocaleDateString('en-US', dateOpts);
    const label =
      exception.end_date && exception.end_date !== exception.date
        ? `${startStr} – ${new Date(exception.end_date).toLocaleDateString('en-US', dateOpts)}`
        : startStr;
    if (window.confirm(`Delete exception for ${label}? This cannot be undone.`)) {
      deleteMutation.mutate(exception.id);
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingException(null);
    queryClient.invalidateQueries({ queryKey: ['schedule-exceptions'] });
  };

  const formatDate = (dateStr: string, short = false) => {
    const date = new Date(dateStr);
    if (short) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  if (isLoading || isLoadingSettings) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-gray-100 rounded-xl" />
        <div className="h-48 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  // Sort exceptions by date
  const sortedExceptions = [...exceptions].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const today = new Date(new Date().toDateString());
  const upcomingExceptions = sortedExceptions.filter(
    e => new Date(e.end_date ?? e.date) >= today
  );
  const pastExceptions = sortedExceptions.filter(
    e => new Date(e.end_date ?? e.date) < today
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Holiday Settings */}
      <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-3 sm:mb-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="text-lg sm:text-xl">🌍</span>
              <span className="truncate">Regional Holidays</span>
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">
              Auto-mark public holidays as closed
            </p>
          </div>
          <button
            onClick={() => updateSettingsMutation.mutate({ enabled: !holidaySettings?.enabled })}
            className={`
              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
              border-2 border-transparent transition-colors duration-200
              ${holidaySettings?.enabled ? 'bg-purple-600' : 'bg-gray-200'}
            `}
          >
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 transform rounded-full
                bg-white shadow transition duration-200
                ${holidaySettings?.enabled ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
        </div>

        {holidaySettings?.enabled && (
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Region
              </label>
              <select
                value={holidaySettings.region_code}
                onChange={(e) => updateSettingsMutation.mutate({ region_code: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                {Object.entries(REGION_NAMES).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            {upcomingHolidays.length > 0 && (
              <div className="bg-purple-50 rounded-xl p-3 sm:p-4">
                <h4 className="text-xs sm:text-sm font-medium text-purple-800 mb-2 sm:mb-3">
                  Upcoming
                </h4>
                <ul className="space-y-1.5 sm:space-y-2">
                  {upcomingHolidays.slice(0, 4).map((holiday, index) => (
                    <li key={index} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <span className="text-purple-500">•</span>
                      <span className="text-purple-900 font-medium flex-shrink-0">
                        {new Date(holiday.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <span className="text-purple-700 truncate">{holiday.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom Exceptions */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Exceptions</h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              Close dates or set modified hours
            </p>
          </div>
          <button
            onClick={handleAddException}
            className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-purple-600 text-white text-sm rounded-xl hover:bg-purple-700 transition-all shadow-sm active:scale-[0.98] flex-shrink-0"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden min-[400px]:inline">Add</span>
          </button>
        </div>

        {exceptions.length === 0 ? (
          <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-12 text-center">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h4 className="text-base sm:text-lg font-medium text-gray-600 mb-1 sm:mb-2">No exceptions</h4>
            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 max-w-xs mx-auto">
              Add exceptions for vacations or special hours.
            </p>
            <button
              onClick={handleAddException}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-xl hover:bg-purple-700 transition-all active:scale-[0.98]"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Exception
            </button>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {/* Upcoming Exceptions */}
            {upcomingExceptions.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden">
                <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700">Upcoming</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {upcomingExceptions.map((exception) => (
                    <ExceptionRow
                      key={exception.id}
                      exception={exception}
                      onEdit={handleEditException}
                      onDelete={handleDeleteException}
                      formatDate={formatDate}
                      formatTime={formatTime}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Past Exceptions */}
            {pastExceptions.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden opacity-60">
                <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500">Past</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {pastExceptions.slice(-5).map((exception) => (
                    <ExceptionRow
                      key={exception.id}
                      exception={exception}
                      onEdit={handleEditException}
                      onDelete={handleDeleteException}
                      formatDate={formatDate}
                      formatTime={formatTime}
                      isPast
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Exception Form Panel */}
      <ExceptionFormPanel
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingException(null);
        }}
        onSuccess={handleFormSuccess}
        editException={editingException}
      />
    </div>
  );
};

// Exception Row Component
interface ExceptionRowProps {
  exception: ScheduleException;
  onEdit: (exception: ScheduleException) => void;
  onDelete: (exception: ScheduleException) => void;
  formatDate: (date: string, short?: boolean) => string;
  formatTime: (minutes: number) => string;
  isPast?: boolean;
}

const ExceptionRow: React.FC<ExceptionRowProps> = ({
  exception,
  onEdit,
  onDelete,
  formatDate,
  formatTime,
  isPast,
}) => {
  return (
    <div className="px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 ${
          exception.is_closed ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
        }`}>
          {exception.is_closed ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="font-medium text-gray-900 text-sm sm:text-base">
              {exception.end_date && exception.end_date !== exception.date ? (
                <>
                  <span className="sm:hidden">
                    {formatDate(exception.date, true)} – {formatDate(exception.end_date, true)}
                  </span>
                  <span className="hidden sm:inline">
                    {formatDate(exception.date)} – {formatDate(exception.end_date)}
                  </span>
                </>
              ) : (
                <>
                  <span className="sm:hidden">{formatDate(exception.date, true)}</span>
                  <span className="hidden sm:inline">{formatDate(exception.date)}</span>
                </>
              )}
            </span>
            {exception.recurring_yearly && (
              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-700">
                Yearly
              </span>
            )}
          </div>
          <div className="text-xs sm:text-sm text-gray-500 truncate">
            {exception.is_closed ? (
              <span className="text-red-600">Closed</span>
            ) : (
              <span className="text-amber-600">
                {formatTime(exception.start_min!)} - {formatTime(exception.end_min!)}
              </span>
            )}
            {exception.reason && (
              <span className="ml-1 sm:ml-2 text-gray-400">• {exception.reason}</span>
            )}
          </div>
        </div>
      </div>

      {!isPast && (
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={() => onEdit(exception)}
            className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Edit"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(exception)}
            className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExceptionsTab;
